import type { PositionedFlag, RoomFlagState, TimelineSegment } from "./focusViewTypes";

export function getLivePosFrac(nowMin: number): number {
  return Math.max(0, (nowMin - 10) / 480);
}

export function getLiveBarPct(nowMin: number): number {
  if (nowMin <= 0) return 0;
  return Math.max(0, ((nowMin - 10) / nowMin) * 100);
}

export function roomFlagState(roomId: string, flags: PositionedFlag[], nowMin: number): RoomFlagState {
  const lp = getLivePosFrac(nowMin);
  const hasActive = flags.some((f) => !f.rv && f.rm.includes(roomId) && f.tlPos >= lp);
  const hasMissed = flags.some((f) => !f.rv && f.rm.includes(roomId) && f.tlPos < lp);
  return { active: hasActive, missed: hasMissed };
}

export function splitRoomFlags(roomId: string, flags: PositionedFlag[], nowMin: number) {
  const lp = getLivePosFrac(nowMin);
  const roomFlags = flags.filter((f) => !f.rv && f.rm.includes(roomId));
  const resolved = flags.filter((f) => f.rv && f.rm.includes(roomId));
  const active = roomFlags.filter((f) => f.tlPos >= lp);
  const missed = roomFlags.filter((f) => f.tlPos < lp);
  return { active, missed, resolved };
}

export function formatCountdown(deadline: number, now: number): { label: string; expired: boolean; warning: boolean } {
  const rem = deadline - now;
  if (rem <= 0) {
    return { label: "Edit window closed", expired: true, warning: false };
  }
  const mins = Math.floor(rem / 60000);
  const secs = Math.floor((rem % 60000) / 1000);
  const label = `${mins}:${secs < 10 ? "0" : ""}${secs} remaining to edit`;
  return { label, expired: false, warning: rem < 2 * 60 * 1000 };
}

export function buildAxisLabels(nowMin: number): string[] {
  const labels: string[] = [];
  for (let li = 0; li < 5; li++) {
    const tm = 9 * 60 + Math.round((nowMin * li) / 4);
    const h = Math.floor(tm / 60);
    const m = tm % 60;
    labels.push(`${h}:${m < 10 ? "0" : ""}${m}`);
  }
  return labels;
}

export function cnrForFlag(
  flag: PositionedFlag,
  cnrBlocks: { lb: string; s: number; e: number }[] | undefined
): string | null {
  if (!cnrBlocks || cnrBlocks.length === 0) return null;
  const flagMin = flag.tlPos * 480;
  for (const c of cnrBlocks) {
    const eMin = c.e < 0 ? 9999 : c.e;
    if (flagMin >= c.s && flagMin < eMin) return c.lb;
  }
  return null;
}

export function flagPipPct(flag: PositionedFlag, nowMin: number): number {
  if (nowMin <= 0) return 0;
  return (flag.tlPos * 480) / nowMin * 100;
}

export function annotateSegments(segs: TimelineSegment[], liveBarPct: number) {
  const totalFlex = segs.reduce((a, s) => (s.t !== "N" ? a + s.f : a), 0);
  let flexSoFar = 0;

  return segs.map((s) => {
    if (s.t === "N") return { ...s, cls: "now" as const };
    const segMidFrac = totalFlex > 0 ? (flexSoFar + s.f / 2) / totalFlex : 0;
    flexSoFar += s.f;

    let cls: string = s.t;
    if (s.t === "f" && segMidFrac < liveBarPct / 100) cls = "f-missed";
    return { ...s, cls };
  });
}

export function formatSelectedRange(selStart: number, selEnd: number, nowMin: number): string {
  const tS = Math.round(Math.min(selStart, selEnd) * nowMin + 9 * 60);
  const tE = Math.round(Math.max(selStart, selEnd) * nowMin + 9 * 60);
  const fmt = (m: number) => `${Math.floor(m / 60)}:${m % 60 < 10 ? "0" : ""}${m % 60}`;
  return `Selected: ${fmt(tS)} – ${fmt(tE)}`;
}


export function calcNowMin(): number {
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  return Math.min(Math.max(cur - 9 * 60, 0), 8 * 60);
}
